import differenceInYears from "date-fns/differenceInYears";
import firebase from "firebase/app";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";

import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { RouteName } from "@/consts/RouteName";
import { SensitiveContentDialogViewEnum } from "@/features/common/components/SensitiveContentDialogView";
import { StandardDialog } from "@/features/common/components/layout/StandardDialog";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import {
  ChannelFragment,
  PostDetailFragment,
} from "@/generated/generated-hooks";

const SensitiveContentDialogView = dynamic(
  () => import("@/features/common/components/SensitiveContentDialogView"),
  { ssr: false },
);

export const SensitiveContentDialog = ({
  channel,
  post,
  logEventSource,
}: { logEventSource?: "post-content" | "channel-page" } & (
  | {
      post: PostDetailFragment;
      channel?: never;
    }
  | {
      post?: never;
      channel: ChannelFragment;
    }
)) => {
  const { ready, user: me } = useUserContext();
  const [open, setOpen] = useState(false);
  const router = useRouter();

  const closeDialog = () => setOpen(false);

  const data = useMemo(() => {
    return !!channel ? getDataFromChannel(channel) : getDataFromPost(post);
  }, [channel, post]);

  useEffect(() => {
    const isDataReady = (!!channel || !!post) && ready;

    if (!isDataReady) return;

    const data: DialogFlowControlData = !!channel
      ? getDataFromChannel(channel)
      : getDataFromPost(post);

    const { writerAccountName, writerId, isSensitive = false, title } = data;

    if (!isSensitive) return;

    /* isOwner */
    if (writerId === me?._id) return;

    /* check if user choose show SensitiveContent */
    if (me?.showSensitiveContent) return;

    firebase
      .analytics()
      .logEvent(AnalyticsEventEnum.SensitiveContentDialogOpen, {
        source: logEventSource,
        channelId: channel?._id,
        channelTitle: channel?.title,
        postId: post?._id,
        postSlug: post?.slug,
      });

    setOpen(true);
  }, [post, channel, me, ready, router.route, logEventSource]);

  const initialView = useMemo(() => {
    if (!me) return SensitiveContentDialogViewEnum.Visitor;

    if (me.birthdate) {
      const age = differenceInYears(new Date(), new Date(me.birthdate));
      if (age < 18) return SensitiveContentDialogViewEnum.Under18;
    }

    return SensitiveContentDialogViewEnum.Setting;
  }, [me]);

  const isChannel = router.route === RouteName.ChannelRoute;
  const name = isChannel
    ? { channelName: data.title }
    : { postTitle: data.title };

  return (
    <StandardDialog open={open} className="max-w-450">
      <SensitiveContentDialogView
        onClose={closeDialog}
        initialView={initialView}
        writerAccountName={data.writerAccountName}
        {...name}
      />
    </StandardDialog>
  );
};

type DialogFlowControlData = {
  isSensitive: boolean;
  writerId: string;
  writerAccountName: string;
  title: string;
};

const getDataFromChannel = (
  channel: ChannelFragment,
): DialogFlowControlData => {
  const {
    isSensitive,
    title,
    creatorId,
    creator: { accountName },
  } = channel;

  return {
    isSensitive,
    title,
    writerId: creatorId,
    writerAccountName: accountName,
  };
};

const getDataFromPost = (post: PostDetailFragment): DialogFlowControlData => {
  const {
    writer: { accountName, _id },
    title,
    isSensitive,
  } = post;
  return {
    isSensitive,
    title,
    writerId: _id,
    writerAccountName: accountName,
  };
};
